import React, { useMemo, useRef, useState } from 'react';
import {
  Activity,
  BatteryCharging,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  CircleStop,
  Dices,
  Droplets,
  Footprints,
  Fuel,
  Gauge,
  HeartPulse,
  Package,
  Pause,
  PenLine,
  Play,
  ShieldAlert,
  Tent,
  Users,
  Wheat,
} from 'lucide-react';

import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { formatDistance } from './hexGridMath';

const copy = {
  fr: {
    step: 'Étape',
    routeProgress: "Progression de l’itinéraire",
    journeys: 'Voir tous les voyages',
    modify: 'Modifier',
    editEvent: 'Éditer le texte MJ',
    saveEvent: 'Terminer l’édition',
    remainingDistance: 'Distance restante',
    remainingDuration: 'Durée estimée',
    previous: 'Précédente',
    next: 'Valider et avancer',
    complete: 'Voyage terminé',
    reroll: "Relancer l’événement",
    halt: 'Halte',
    noHalt: 'Aucune halte prévue à cette étape.',
    noEvent: 'Rien de notable',
    eventEdited: 'Texte MJ conservé',
    play: 'Animer le groupe',
    pause: 'Mettre l’animation en pause',
    state: 'État du groupe',
  },
  en: {
    step: 'Step',
    routeProgress: 'Route progress',
    journeys: 'View all journeys',
    modify: 'Modify',
    editEvent: 'Edit GM text',
    saveEvent: 'Finish editing',
    remainingDistance: 'Distance remaining',
    remainingDuration: 'Estimated duration',
    previous: 'Previous',
    next: 'Validate and advance',
    complete: 'Journey complete',
    reroll: 'Reroll event',
    halt: 'Halt',
    noHalt: 'No halt planned for this step.',
    noEvent: 'Nothing notable',
    eventEdited: 'GM text preserved',
    play: 'Animate the party',
    pause: 'Pause the animation',
    state: 'Party state',
  },
};

const STATE_ICONS = {
  food: Wheat,
  fodder: Wheat,
  water: Droplets,
  potable_water: Droplets,
  fuel: Fuel,
  credits: CircleDollarSign,
  money: CircleDollarSign,
  charge: BatteryCharging,
  battery: BatteryCharging,
  fatigue: Footprints,
  equipment: Package,
  vehicle: Gauge,
  wounds: HeartPulse,
  alert: ShieldAlert,
  trace: Activity,
};

const localLabel = (item, language) => (
  item?.label?.[language] || item?.label?.fr || item?.label?.en || item?.id || ''
);

const formatHours = (hours, language) => {
  if (!Number.isFinite(hours) || hours <= 0) return '—';
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} min`;
  if (hours < 24) return `${hours.toLocaleString(language, { maximumFractionDigits: 1 })} h`;
  return `${(hours / 24).toLocaleString(language, { maximumFractionDigits: 1 })} j`;
};

const stateAtStep = (run, stepIndex) => (
  stepIndex <= 0 ? run.steps[0].stateBefore : run.steps[stepIndex - 1].stateAfter
);

const threadsAtStep = (run, stepIndex) => (
  stepIndex <= 0 ? [] : run.steps[stepIndex - 1].activeThreadsAfter
);

export function TravelRunCommandCenter({
  language = 'fr',
  distanceUnit = 'km',
  pack,
  run,
  accentColor = '#B8864B',
  animationProgress = 0,
  animationPlaying = false,
  map,
  onOpenJourneys,
  onModify,
  onRunChange,
  onRerollEvent,
  onAnimationProgressChange,
  onAnimationPlayingChange,
}) {
  const text = copy[language] || copy.fr;
  const [editingEvent, setEditingEvent] = useState(false);
  const narrationRef = useRef(null);
  const step = run.steps[run.currentStepIndex] || run.steps[0];
  const event = pack.events.find((item) => item.id === step?.eventId);
  const environment = pack.environment.states.find((item) => item.id === step?.environmentId);
  const halt = pack.halts.find((item) => item.id === step?.haltId);
  const progress = run.steps.length ? ((run.currentStepIndex + 1) / run.steps.length) * 100 : 0;
  const remaining = useMemo(() => run.steps.slice(run.currentStepIndex).reduce((total, item) => ({
    distance: total.distance + (item.distanceMeters || 0),
    hours: total.hours + (item.durationHours || 0),
  }), { distance: 0, hours: 0 }), [run.currentStepIndex, run.steps]);

  const moveToStep = (nextIndex) => {
    const boundedIndex = Math.max(0, Math.min(run.steps.length - 1, nextIndex));
    onRunChange({
      ...run,
      currentStepIndex: boundedIndex,
      currentState: stateAtStep(run, boundedIndex),
      activeThreads: threadsAtStep(run, boundedIndex),
      status: boundedIndex < run.steps.length ? 'in_progress' : run.status,
    });
    setEditingEvent(false);
  };

  const advance = () => {
    const last = run.currentStepIndex >= run.steps.length - 1;
    onRunChange({
      ...run,
      currentStepIndex: last ? run.currentStepIndex : run.currentStepIndex + 1,
      currentState: step.stateAfter,
      activeThreads: step.activeThreadsAfter,
      status: last ? 'complete' : 'in_progress',
    });
    setEditingEvent(false);
  };

  const toggleAnimation = () => {
    if (!animationPlaying && animationProgress >= 1) onAnimationProgressChange(0);
    onAnimationPlayingChange(!animationPlaying);
  };

  const updateNarration = (value) => {
    onRunChange({
      ...run,
      steps: run.steps.map((item) => item.index === step.index ? {
        ...item,
        eventNarration: value,
        editedFields: { ...(item.editedFields || {}), eventNarration: value },
        lockedFields: [...new Set([...(item.lockedFields || []), 'eventNarration'])],
      } : item),
    });
  };

  const stepTitle = localLabel(event, language) || localLabel(environment, language) || text.noEvent;

  return (
    <div className="flex h-[calc(100vh-4rem)] min-h-[640px] flex-col overflow-hidden bg-[#0c1218]" data-testid="travel-run-command-center">
      <header className="relative z-40 flex min-h-[74px] shrink-0 items-center gap-4 border-b border-white/10 bg-[#0b1219]/95 px-4 text-slate-100 shadow-xl shadow-black/10 xl:px-6">
        <Button
          type="button"
          variant="outline"
          className="h-11 min-w-0 max-w-[280px] justify-start gap-3 border-white/10 bg-white/[0.035] px-3 text-slate-100 hover:bg-white/[0.08] hover:text-white"
          onClick={onOpenJourneys}
          aria-label={text.journeys}
        >
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border" style={{ color: accentColor, borderColor: `${accentColor}55`, backgroundColor: `${accentColor}12` }}>
            <Users className="h-4 w-4" />
          </span>
          <span className="truncate text-sm font-medium">{run.party?.name}</span>
          <ChevronDown className="ml-auto h-4 w-4 text-slate-400" />
        </Button>

        <div className="hidden h-9 w-px bg-white/10 md:block" />

        <div className="w-[130px] shrink-0 leading-tight">
          <p className="text-sm font-medium">{text.step} {run.currentStepIndex + 1} / {run.steps.length}</p>
          <p className="mt-1 truncate text-xs font-semibold" style={{ color: accentColor }}>{stepTitle}</p>
        </div>

        <div className="hidden h-9 w-px bg-white/10 lg:block" />

        <div className="hidden min-w-0 flex-1 items-center gap-4 lg:flex">
          <span className="shrink-0 text-xs text-slate-400">{text.routeProgress}</span>
          <div className="relative flex min-w-0 flex-1 items-center justify-between" role="group" aria-label={text.routeProgress}>
            <div className="absolute left-2 right-2 top-1/2 h-0.5 -translate-y-1/2 bg-white/10" />
            <div className="absolute left-2 top-1/2 h-0.5 -translate-y-1/2 transition-[width] duration-500" style={{ width: `calc(${progress}% - 1rem)`, backgroundColor: accentColor }} />
            {run.steps.map((item, index) => {
              const played = index < run.currentStepIndex;
              const current = index === run.currentStepIndex;
              return (
                <button
                  key={item.id}
                  type="button"
                  className="relative z-10 flex h-7 w-7 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                  onClick={() => moveToStep(index)}
                  aria-label={`${text.step} ${index + 1}`}
                  aria-current={current ? 'step' : undefined}
                >
                  <span
                    className={`block rounded-full border-2 transition-all ${current ? 'h-4 w-4 ring-4 ring-black/35' : 'h-3.5 w-3.5'} ${played || current ? 'border-slate-200' : 'border-slate-400 bg-[#0b1219]'}`}
                    style={played || current ? { backgroundColor: accentColor } : undefined}
                  />
                </button>
              );
            })}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="shrink-0 text-slate-300 hover:bg-white/10 hover:text-white"
            onClick={toggleAnimation}
            aria-label={animationPlaying ? text.pause : text.play}
            title={animationPlaying ? text.pause : text.play}
          >
            {animationPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </Button>
        </div>

        <Button type="button" variant="outline" className="ml-auto shrink-0 border-white/10 bg-white/[0.035] text-slate-100 hover:bg-white/[0.08] hover:text-white" onClick={onModify}>
          <PenLine className="h-4 w-4" />
          <span className="hidden sm:inline">{text.modify}</span>
        </Button>
      </header>

      <div className="relative min-h-0 flex-1 overflow-hidden">
        <div className="absolute inset-0 [&>section]:h-full [&>section]:min-h-0">
          {map}
        </div>

        <aside
          className="absolute left-5 top-[43%] z-30 hidden max-h-[calc(100%-17rem)] w-[154px] -translate-y-1/2 overflow-y-auto rounded-xl border border-white/10 bg-[#0b1117]/90 p-2 text-slate-100 shadow-2xl shadow-black/35 backdrop-blur-md lg:block"
          aria-label={text.state}
          data-testid="travel-run-state-hud"
        >
          {pack.stateFields.map((field, index) => {
            const Icon = STATE_ICONS[field.id] || Activity;
            const value = run.currentState[field.id];
            const unit = field.unit?.[language] || field.unit?.fr || '';
            return (
              <div key={field.id} className={`flex items-center gap-2.5 px-1.5 py-2.5 ${index ? 'border-t border-white/10' : ''}`}>
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border" style={{ color: accentColor, borderColor: `${accentColor}45`, backgroundColor: `${accentColor}0f` }}>
                  <Icon className="h-[18px] w-[18px]" />
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-[11px] text-slate-300">{localLabel(field, language)}</span>
                  <span className="mt-0.5 block text-base font-medium leading-none">{String(value ?? '—')}{unit === '%' ? '%' : ''}</span>
                </span>
              </div>
            );
          })}
        </aside>

        <section className="absolute inset-x-3 bottom-3 z-30 overflow-hidden rounded-xl border border-white/15 bg-[#0b1117]/95 text-slate-100 shadow-2xl shadow-black/55 backdrop-blur-lg lg:inset-x-5" data-testid="travel-run-step-console" aria-live="polite">
          <div className="relative hidden h-10 items-center px-5 lg:flex">
            <div className="absolute inset-x-5 top-1/2 h-px bg-white/10" />
            <div className="relative z-10 flex w-full items-center justify-between">
              {run.steps.map((item, index) => {
                const current = index === run.currentStepIndex;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className="group flex items-center gap-2 bg-[#0b1117] pr-3 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                    onClick={() => moveToStep(index)}
                    aria-current={current ? 'step' : undefined}
                  >
                    <span className="flex h-6 w-6 items-center justify-center rounded-full border text-[11px] font-semibold transition-colors" style={current ? { color: '#fff', borderColor: accentColor, backgroundColor: accentColor } : { color: '#cbd5e1', borderColor: '#475569', backgroundColor: '#0b1117' }}>{index + 1}</span>
                    {current && <span className="font-semibold" style={{ color: accentColor }}>{text.step} {index + 1}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid min-h-[188px] grid-cols-1 lg:grid-cols-[290px_minmax(0,1fr)_340px]">
            <div className="border-b border-white/10 px-5 py-4 lg:border-b-0 lg:border-r">
              <h2 className="font-cinzel text-lg font-bold leading-tight xl:text-xl">{text.step} {step.index + 1} · {stepTitle}</h2>
              <div className="mt-3 h-px w-full" style={{ backgroundColor: `${accentColor}aa` }} />
              <dl className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-1">
                <div className="flex items-center gap-3">
                  <Gauge className="h-5 w-5 text-slate-400" />
                  <div><dt className="text-[11px] text-slate-400">{text.remainingDistance}</dt><dd className="text-sm font-semibold">{formatDistance(remaining.distance, distanceUnit, language)}</dd></div>
                </div>
                <div className="flex items-center gap-3">
                  <Activity className="h-5 w-5 text-slate-400" />
                  <div><dt className="text-[11px] text-slate-400">{text.remainingDuration}</dt><dd className="text-sm font-semibold">{formatHours(remaining.hours, language)}</dd></div>
                </div>
              </dl>
            </div>

            <div className="min-w-0 border-b border-white/10 px-5 py-4 lg:border-b-0 lg:border-r">
              <div className="flex items-start gap-3">
                <Dices className="mt-0.5 h-5 w-5 shrink-0" style={{ color: accentColor }} />
                <div className="min-w-0 flex-1">
                  {editingEvent ? (
                    <Textarea
                      ref={narrationRef}
                      autoFocus
                      className="min-h-[72px] resize-none border-white/15 bg-white/[0.035] text-sm leading-relaxed text-slate-100"
                      value={step.eventNarration || ''}
                      onChange={(eventChange) => updateNarration(eventChange.target.value)}
                    />
                  ) : (
                    <p className="text-base leading-relaxed">{step.eventNarration || text.noEvent}</p>
                  )}
                  <p className="mt-2 text-sm leading-relaxed text-slate-300">{step.atmosphere}</p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 text-slate-400 hover:bg-white/10 hover:text-white"
                  onClick={() => setEditingEvent((current) => !current)}
                  aria-label={editingEvent ? text.saveEvent : text.editEvent}
                  title={editingEvent ? text.saveEvent : text.editEvent}
                >
                  <PenLine className="h-4 w-4" />
                </Button>
              </div>
              {step.lockedFields?.includes('eventNarration') && <Badge variant="outline" className="ml-8 mt-2 border-white/15 text-[10px] text-slate-300">{text.eventEdited}</Badge>}
              <div className="mt-3 flex items-start gap-3 border-t border-white/10 pt-3">
                <Tent className="h-5 w-5 shrink-0" style={{ color: accentColor }} />
                <p className="min-w-16 text-xs font-semibold uppercase tracking-[0.12em]" style={{ color: accentColor }}>{text.halt}</p>
                <p className="text-sm text-slate-300">{step.haltId ? `${localLabel(halt, language)} · ${step.haltNarration}` : text.noHalt}</p>
              </div>
            </div>

            <div className="flex flex-col justify-center gap-3 px-5 py-4">
              <Button
                type="button"
                className="h-12 w-full text-sm font-semibold text-white shadow-lg"
                style={{ backgroundColor: accentColor }}
                disabled={run.status === 'complete'}
                onClick={advance}
              >
                {run.status === 'complete' ? <CircleStop className="h-5 w-5" /> : null}
                {run.status === 'complete' ? text.complete : text.next}
                {run.status !== 'complete' ? <ChevronRight className="ml-auto h-5 w-5" /> : null}
              </Button>
              <div className="grid grid-cols-2 gap-2">
                <Button type="button" variant="outline" className="h-11 border-white/10 bg-white/[0.025] text-slate-200 hover:bg-white/[0.08] hover:text-white" disabled={run.currentStepIndex <= 0} onClick={() => moveToStep(run.currentStepIndex - 1)}>
                  <ChevronLeft className="h-4 w-4" />{text.previous}
                </Button>
                <Button type="button" variant="outline" className="h-11 border-white/10 bg-white/[0.025] text-slate-200 hover:bg-white/[0.08] hover:text-white" disabled={step.index < run.currentStepIndex} onClick={() => onRerollEvent(step.index)}>
                  <Dices className="h-4 w-4" />{text.reroll}
                </Button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default TravelRunCommandCenter;
